import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

/**
 * Converts a PascalCase or snake_case string to camelCase.
 */
function toCamel(str: string): string {
  // Handle PascalCase → camelCase
  if (/^[A-Z]/.test(str)) {
    return str.charAt(0).toLowerCase() + str.slice(1);
  }
  // Handle snake_case → camelCase
  return str.replace(/_([a-z])/g, (_, ch) => ch.toUpperCase());
}

/**
 * Recursively converts all object keys to camelCase.
 * Leaves arrays, primitives, and null/undefined untouched element-wise.
 */
function deepCamelCase(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(deepCamelCase);
  }
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([k, v]) => [
        toCamel(k),
        deepCamelCase(v),
      ])
    );
  }
  return value;
}

/**
 * HTTP response interceptor that deep-converts all JSON response bodies
 * from PascalCase / snake_case to camelCase automatically.
 *
 * Registered globally in app.config.ts so every HTTP call benefits.
 * Does NOT mutate requests or non-JSON responses.
 */
@Injectable()
export class CamelCaseInterceptor implements HttpInterceptor {
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      map((event) => {
        if (
          event instanceof HttpResponse &&
          event.body !== null &&
          typeof event.body === "object" &&
          !(event.body instanceof Blob) &&
          !(event.body instanceof ArrayBuffer)
        ) {
          return event.clone({ body: deepCamelCase(event.body) });
        }
        return event;
      })
    );
  }
}
